import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { History } from "lucide-react";
import { adminAction } from "@/lib/adminApi";
import { supabase } from "@/integrations/supabase/client";
import {
  AdminPageHeader,
  AdminPanel,
  AdminPageSkeleton,
  AdminSection,
  AdminTableSkeleton,
  AdminEmptyState,
  AdminHeroMetric,
  AdminHeroAsideStat,
  AdminPill,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from "./AdminUI";

function statusTone(s?: string): "success" | "danger" | "info" | "neutral" {
  const v = s?.toLowerCase();
  if (v === "active") return "success";
  if (v === "cancelled" || v === "canceled") return "danger";
  if (v === "trialing") return "info";
  return "neutral";
}

function eventTone(e?: string): "success" | "info" | "warning" | "danger" | "neutral" {
  switch (e) {
    case "created": return "success";
    case "upgraded": return "info";
    case "downgraded": return "warning";
    case "cancelled": return "danger";
    default: return "neutral";
  }
}

export function AdminSubscriptionsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "subscriptions"],
    queryFn: () => adminAction("subscriptions"),
  });

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ["admin", "subscription_history"],
    queryFn: () => adminAction("subscription_history"),
  });

  // MRR is the single Stripe-live figure from admin-metrics — same
  // source the /admin dashboard hero reads from.
  const { data: metrics } = useQuery({
    queryKey: ["admin-metrics"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-metrics", { body: {} });
      if (error) throw error;
      return data;
    },
  });

  const stats = useMemo(() => {
    const subs = data || [];
    const active = subs.filter((s: any) => s.status === "active");
    const cancelledThisMonth = subs.filter((s: any) => {
      if (!["cancelled", "canceled"].includes(s.status)) return false;
      const end = new Date(s.current_period_end);
      const now = new Date();
      return end.getMonth() === now.getMonth() && end.getFullYear() === now.getFullYear();
    });
    const mrrCents = metrics?.weekly?.mrrCents ?? 0;
    const currency = (metrics?.weekly?.mrrCurrency ?? "usd").toUpperCase();
    const mrr = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(mrrCents / 100);
    return {
      active: active.length,
      cancelled: cancelledThisMonth.length,
      mrr,
    };
  }, [data, metrics]);

  if (isLoading) return <AdminPageSkeleton stats={3} rows={6} columns={5} />;

  return (
    <div className="space-y-8">
      <AdminPageHeader
        eyebrow="Revenue"
        title="Subscriptions"
        description="Monitor active subscriptions, recent changes, and recurring revenue."
      />

      <AdminHeroMetric
        eyebrow="Monthly Recurring Revenue"
        value={stats.mrr}
        caption="Stripe-live snapshot of every active subscription, normalised to monthly."
        tone="success"
        aside={
          <>
            <AdminHeroAsideStat label="Active" value={stats.active} />
            <AdminHeroAsideStat label="Cancelled · MTD" value={stats.cancelled} />
          </>
        }
      />

      <AdminSection title="Current subscriptions">
        <AdminPanel>
          <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <thead style={{ borderBottom: "1px solid rgba(58,53,48,0.06)" }}>
              <tr>
                <th className={adminTableHeadCellClass}>Organisation</th>
                <th className={adminTableHeadCellClass}>Plan</th>
                <th className={adminTableHeadCellClass}>Status</th>
                <th className={adminTableHeadCellClass}>Seats</th>
                <th className={adminTableHeadCellClass}>Period end</th>
              </tr>
            </thead>
            <tbody>
              {(data || []).map((s: any, i: number) => (
                <tr key={s.id} className={adminTableRowClass} style={{ borderTop: i === 0 ? "none" : "1px solid rgba(58,53,48,0.06)" }}>
                  <td className={adminTableCellClass + " text-foreground"} style={{ fontWeight: 500 }}>{s.org_name}</td>
                  <td className={adminTableCellClass}><AdminPill tone="neutral">{s.plan_type || "—"}</AdminPill></td>
                  <td className={adminTableCellClass}><AdminPill tone={statusTone(s.status)}>{s.status}</AdminPill></td>
                  <td className={adminTableCellClass + " text-foreground tabular-nums"}>{s.seat_limit ?? "—"}</td>
                  <td className={adminTableCellClass + " text-muted-foreground tabular-nums"}>
                    {s.current_period_end ? new Date(s.current_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminPanel>
      </AdminSection>

      <AdminSection title="Subscription history" description="Recent plan changes and cancellations.">
        {historyLoading ? (
          <AdminTableSkeleton rows={4} columns={4} />
        ) : (history || []).length === 0 ? (
          <AdminEmptyState icon={History} title="No history yet" description="Plan changes will appear here as they happen." />
        ) : (
          <AdminPanel>
            <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <thead style={{ borderBottom: "1px solid rgba(58,53,48,0.06)" }}>
                <tr>
                  <th className={adminTableHeadCellClass}>Date</th>
                  <th className={adminTableHeadCellClass}>Organisation</th>
                  <th className={adminTableHeadCellClass}>Event</th>
                  <th className={adminTableHeadCellClass}>From → To</th>
                </tr>
              </thead>
              <tbody>
                {(history || []).map((e: any, i: number) => (
                  <tr key={e.id} className={adminTableRowClass} style={{ borderTop: i === 0 ? "none" : "1px solid rgba(58,53,48,0.06)" }}>
                    <td className={adminTableCellClass + " text-muted-foreground tabular-nums"}>
                      {new Date(e.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className={adminTableCellClass + " text-foreground"} style={{ fontWeight: 500 }}>{e.org_name}</td>
                    <td className={adminTableCellClass}><AdminPill tone={eventTone(e.event_type)}>{e.event_type}</AdminPill></td>
                    <td className={adminTableCellClass + " text-foreground capitalize"}>{e.from_plan || "—"} → {e.to_plan || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminPanel>
        )}
      </AdminSection>
    </div>
  );
}
